import { useMemo, useState } from 'react';
import { listTemplates } from '@fotoboek/templates';
import { useProjectStore } from '../../../state/projectStore';
import { EmptyState } from '../../ui/EmptyState';
import { t } from '../../../i18n';
import './templates-tab.css';

const TEMPLATES = listTemplates({ pageType: 'inner' });

export function TemplatesTab() {
  const project = useProjectStore((s) => s.project);
  const view = useProjectStore((s) => s.view);
  const selection = useProjectStore((s) => s.selection);
  const applyTemplateToPage = useProjectStore((s) => s.applyTemplateToPage);
  const [redistribute, setRedistribute] = useState(true);

  const targetPageId = useMemo(() => {
    if (selection.target?.scope === 'page') return selection.target.pageId;
    if (view.mode === 'spread' && project) {
      const leftPage = project.pages.find((p) => p.order === view.spreadIndex * 2);
      return leftPage?.id ?? null;
    }
    return null;
  }, [selection.target, view, project]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof TEMPLATES>();
    for (const template of TEMPLATES) {
      const list = map.get(template.category) ?? [];
      list.push(template);
      map.set(template.category, list);
    }
    return map;
  }, []);

  if (!targetPageId) {
    return (
      <div className="left-panel-tab-body">
        <EmptyState title="Selecteer eerst een pagina om een template toe te passen." />
      </div>
    );
  }

  return (
    <div className="left-panel-tab-body templates-tab">
      <label className="templates-redistribute">
        <input
          type="checkbox"
          checked={redistribute}
          onChange={(e) => setRedistribute(e.target.checked)}
        />
        {t('editor.templates.redistribute')}
      </label>
      {[...grouped.entries()].map(([category, templates]) => (
        <div key={category}>
          <div className="left-panel-section-title">
            {t(`editor.templates.categories.${category}` as string) || category}
          </div>
          <div className="templates-grid">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                className="template-card"
                title={t('editor.templates.applyToPage')}
                onClick={() => applyTemplateToPage(targetPageId, template.id, redistribute)}
              >
                <TemplatePreview elements={template.elements} pageSize={template.pageSizeMm} />
                <span>{template.name}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TemplatePreview({
  elements,
  pageSize,
}: {
  elements: Array<{ type: string; x: number; y: number; width: number; height: number }>;
  pageSize: { width: number; height: number };
}) {
  return (
    <svg viewBox={`0 0 ${pageSize.width} ${pageSize.height}`} className="template-preview">
      <rect
        x={0}
        y={0}
        width={pageSize.width}
        height={pageSize.height}
        fill="#ffffff"
        stroke="#e2ddd3"
      />
      {elements.map((el, i) => (
        <rect
          key={i}
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          fill={el.type === 'photoFrame' ? '#dcd6c9' : el.type === 'text' ? 'none' : '#c8beae'}
          stroke={el.type === 'text' ? '#918a7d' : 'none'}
        />
      ))}
    </svg>
  );
}
